"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react"
import { usePathname } from "next/navigation"
import { useLocale } from "next-intl"
import { useCountry } from "@/components/layout/country-provider"
import { track } from "@/lib/analytics"

export type SearchSource =
  | "navbar-desktop"
  | "navbar-mobile"
  | "shortcut"
  | "page"

interface SearchOpenOptions {
  /** Skip the modal's enter animation. Used when the search surface
   *  visually replaces another full-screen surface (e.g. the mobile menu)
   *  so the user perceives a direct hand-off, not two stacked transitions. */
  instant?: boolean
  /** Where the user opened the modal from. Read by the modal so
   *  search_query events carry the same source as search_opened. */
  source?: SearchSource
}

interface SearchContextValue {
  isOpen: boolean
  /** True when the current open() was called with `{ instant: true }`.
   *  Resets to false on close. Modal reads it to skip enter animation. */
  openedInstant: boolean
  /** The source the modal was opened from. Null until first open. */
  openedSource: SearchSource | null
  open: (options?: SearchOpenOptions) => void
  close: () => void
  toggle: (options?: { source?: SearchSource }) => void
  /** Ella side-drawer state, driven from the modal's "Ask AI" mode. Lives here
   *  (not in the modal) because the drawer must outlive the modal: picking a
   *  question closes the modal and hands off to the drawer. */
  aiDrawer: { open: boolean; question: string | null; nonce: number }
  /** Open the Ella drawer with a question and close the search modal. */
  askAi: (question: string) => void
  closeAiDrawer: () => void
}

const SearchContext = createContext<SearchContextValue | null>(null)

/**
 * Custom event the /search page listens for to focus its own input instead
 * of letting the modal open on top of the results page. Decouples the
 * provider (which knows nothing about the page route) from the page
 * itself. Same trick mobile-friendly nav patterns use to broadcast intent
 * across React subtrees without prop-drilling.
 */
export const SEARCH_FOCUS_EVENT = "ahead:search-focus"

function isOnSearchPage(pathname: string): boolean {
  // Path shape: /{country}/{locale}/search — no nested children today,
  // but match `/search` segment-aware so we don't false-positive on
  // /searcher etc.
  return /\/search(?:\?|$)/.test(pathname)
}

export function SearchProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [openedInstant, setOpenedInstant] = useState(false)
  const [openedSource, setOpenedSource] = useState<SearchSource | null>(null)
  const pathname = usePathname()
  const locale = useLocale()
  const { code: country } = useCountry()

  const open = useCallback(
    (options?: SearchOpenOptions) => {
      // On /search the page already has a search input visible — opening the
      // modal on top of the results would stack two search UIs and hide the
      // very thing the user is looking at. Broadcast a focus event instead;
      // the page picks it up and focuses its own input.
      if (isOnSearchPage(pathname)) {
        window.dispatchEvent(new CustomEvent(SEARCH_FOCUS_EVENT))
        return
      }
      setOpenedInstant(options?.instant === true)
      setOpenedSource(options?.source ?? null)
      setIsOpen(true)
    },
    [pathname]
  )

  const close = useCallback(() => {
    setIsOpen(false)
    setOpenedInstant(false)
    setOpenedSource(null)
  }, [])

  const [aiDrawer, setAiDrawer] = useState<{
    open: boolean
    question: string | null
    nonce: number
  }>({ open: false, question: null, nonce: 0 })

  const askAi = useCallback((question: string) => {
    setAiDrawer((s) => ({ open: true, question, nonce: s.nonce + 1 }))
    // Hand off from the modal to the side drawer.
    setIsOpen(false)
    setOpenedInstant(false)
    setOpenedSource(null)
  }, [])

  const closeAiDrawer = useCallback(() => {
    setAiDrawer((s) => ({ ...s, open: false }))
  }, [])

  const toggle = useCallback(
    (options?: { source?: SearchSource }) => {
      if (isOnSearchPage(pathname)) {
        window.dispatchEvent(new CustomEvent(SEARCH_FOCUS_EVENT))
        return
      }
      setOpenedInstant(false)
      setOpenedSource(options?.source ?? null)
      setIsOpen((v) => !v)
    },
    [pathname]
  )

  // Close on navigation — same convention as the navbar's mobile menu and
  // dropdowns, so a result click never leaves a stale modal hanging.
  useEffect(() => {
    setIsOpen(false)
    setOpenedInstant(false)
    setOpenedSource(null)
  }, [pathname])

  // Cmd-K / Ctrl-K opens search from anywhere on the page. ⌘ on macOS,
  // Ctrl elsewhere. Skip when typing in an input/textarea/contenteditable
  // (preserves browser shortcuts inside the search input itself).
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (
        (event.key === "k" || event.key === "K") &&
        (event.metaKey || event.ctrlKey)
      ) {
        const target = event.target as HTMLElement | null
        const tag = target?.tagName
        if (
          tag === "INPUT" ||
          tag === "TEXTAREA" ||
          target?.isContentEditable
        ) {
          return
        }
        event.preventDefault()
        if (isOnSearchPage(pathname)) {
          window.dispatchEvent(new CustomEvent(SEARCH_FOCUS_EVENT))
          return
        }
        if (!isOpen) {
          track.searchOpened({ source: "shortcut", country, locale })
        }
        setOpenedSource("shortcut")
        setIsOpen((v) => !v)
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [pathname, isOpen, country, locale])

  const value = useMemo(
    () => ({
      isOpen,
      openedInstant,
      openedSource,
      open,
      close,
      toggle,
      aiDrawer,
      askAi,
      closeAiDrawer,
    }),
    [
      isOpen,
      openedInstant,
      openedSource,
      open,
      close,
      toggle,
      aiDrawer,
      askAi,
      closeAiDrawer,
    ]
  )

  return (
    <SearchContext.Provider value={value}>{children}</SearchContext.Provider>
  )
}

export function useSearchModal(): SearchContextValue {
  const ctx = useContext(SearchContext)
  if (!ctx) {
    throw new Error("useSearchModal must be used within <SearchProvider>")
  }
  return ctx
}
